import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDropzone } from 'react-dropzone';
import { Upload, ArrowLeft, FileText, CheckCircle, XCircle, AlertTriangle, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const DOCUMENT_TYPES = [
  { value: 'certificate_of_origin', label: 'Certificate of Origin' },
  { value: 'assay_report', label: 'Assay Report' },
  { value: 'mining_license', label: 'Mining License' },
  { value: 'export_permit', label: 'Export Permit' },
  { value: 'quality_certificate', label: 'Quality Certificate' },
  { value: 'environmental_compliance', label: 'Environmental Compliance' },
  { value: 'tax_clearance', label: 'Tax Clearance' },
  { value: 'company_registration', label: 'Company Registration' },
  { value: 'other', label: 'Other Document' },
];

export default function DocumentUpload() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [minerals, setMinerals] = useState([]);
  const [selectedMineral, setSelectedMineral] = useState('');
  const [uploadData, setUploadData] = useState({
    document_type: 'assay_report',
    document_name: '',
    expiry_date: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fetchMinerals = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/minerals/seller/my-listings`, {
        headers: getAuthHeader()
      });
      setMinerals(response.data);
    } catch (error) {
      toast.error('Failed to fetch minerals');
    }
  }, [getAuthHeader]);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/documents/mineral/${selectedMineral}`, {
        headers: getAuthHeader()
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents');
    }
  }, [selectedMineral, getAuthHeader]);

  useEffect(() => {
    if (!user || user.role !== 'seller') {
      toast.error('Only sellers can access this page');
      navigate('/');
      return;
    }
    fetchMinerals();
  }, [user, navigate, fetchMinerals]);

  useEffect(() => {
    const mineralId = searchParams.get('mineral');
    if (mineralId) {
      setSelectedMineral(mineralId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedMineral) {
      fetchDocuments();
    }
  }, [selectedMineral, fetchDocuments]);

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      if (!uploadData.document_name) {
        setUploadData(prev => ({ ...prev, document_name: acceptedFiles[0].name }));
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: false
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedMineral) {
      toast.error('Please select a mineral listing');
      return;
    }
    
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('mineral_id', selectedMineral);
      formData.append('document_type', uploadData.document_type);
      formData.append('document_name', uploadData.document_name || selectedFile.name);
      if (uploadData.expiry_date) {
        formData.append('expiry_date', uploadData.expiry_date);
      }

      const response = await axios.post(`${API_URL}/documents/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Document uploaded successfully!');
      
      if (response.data.is_expired) {
        toast.warning('Note: This document appears to be expired based on the expiry date provided.');
      }

      // Reset form
      setSelectedFile(null);
      setUploadData({
        document_type: 'assay_report',
        document_name: '',
        expiry_date: ''
      });
      
      // Refresh documents list
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/documents/${documentId}`, {
        headers: getAuthHeader()
      });
      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getDocumentTypeLabel = (type) => {
    const doc = DOCUMENT_TYPES.find(d => d.value === type);
    return doc ? doc.label : type;
  };

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate('/seller')}
          className="inline-flex items-center text-[#94A3B8] hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </button>

        <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#F59E0B]" />
            {t('doc.title')}
          </h1>
          <p className="text-[#94A3B8] mb-8">
            {t('doc.subtitle')}
          </p>

          {/* Mineral Selection */}
          <div className="mb-8">
            <Label className="text-white font-medium">Select Mineral Listing *</Label>
            <Select
              value={selectedMineral}
              onValueChange={setSelectedMineral}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Choose a mineral listing..." />
              </SelectTrigger>
              <SelectContent>
                {minerals.map((mineral) => (
                  <SelectItem key={mineral.id} value={mineral.id}>
                    {mineral.title} - {mineral.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedMineral && (
            <>
              {/* Upload Form */}
              <div className="border-t border-[#1E293B] pt-8 mb-8">
                <h2 className="text-xl font-semibold text-white mb-4">{t('doc.upload')}</h2>
                
                <form onSubmit={handleUpload} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="document_type" className="text-white font-medium">Document Type *</Label>
                      <Select
                        value={uploadData.document_type}
                        onValueChange={(value) => setUploadData({ ...uploadData, document_type: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="document_name" className="text-white font-medium">Document Name</Label>
                      <Input
                        id="document_name"
                        value={uploadData.document_name}
                        onChange={(e) => setUploadData({ ...uploadData, document_name: e.target.value })}
                        placeholder="Enter document name..."
                        className="mt-1 border-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="expiry_date" className="text-white font-medium">Expiry Date (Optional)</Label>
                      <Input
                        id="expiry_date"
                        type="date"
                        value={uploadData.expiry_date}
                        onChange={(e) => setUploadData({ ...uploadData, expiry_date: e.target.value })}
                        className="mt-1 border-[#1E293B]"
                      />
                      <p className="text-xs text-[#64748B] mt-1">
                        System will flag documents that have expired
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white font-medium mb-2 block">Document File *</Label>
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                        isDragActive ? 'border-amber-500 bg-amber-50' : 'border-[#1E293B] hover:border-slate-400'
                      }`}
                    >
                      <input {...getInputProps()} />
                      <Upload className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                      {selectedFile ? (
                        <div>
                          <p className="text-[#10B981] font-medium">
                            <CheckCircle className="w-4 h-4 inline mr-2" />
                            {selectedFile.name}
                          </p>
                          <p className="text-sm text-[#64748B] mt-1">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      ) : (
                        <>
                          <p className="text-[#94A3B8]">
                            {isDragActive ? 'Drop file here...' : 'Drag & drop document, or click to select'}
                          </p>
                          <p className="text-sm text-[#64748B] mt-2">PDF, DOC, DOCX, PNG, JPG up to 10MB</p>
                        </>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !selectedFile}
                    className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
                  >
                    {loading ? 'Uploading...' : 'Upload Document'}
                  </Button>
                </form>
              </div>

              {/* Existing Documents */}
              <div className="border-t border-[#1E293B] pt-8">
                <h2 className="text-xl font-semibold text-white mb-4">Uploaded Documents</h2>
                
                {documents.length === 0 ? (
                  <div className="text-center py-8 bg-[#0B1120] rounded-xl border border-[#1E293B]">
                    <FileText className="w-12 h-12 text-[#1E293B] mx-auto mb-3" />
                    <p className="text-[#64748B]">No documents uploaded yet</p>
                    <p className="text-sm text-[#64748B] mt-1">
                      Upload compliance documents to build buyer trust
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div 
                        key={doc.id} 
                        className={`flex items-center justify-between p-4 rounded-xl border ${
                          doc.is_expired 
                            ? 'bg-red-50 border-red-200' 
                            : doc.verified_by_admin 
                              ? 'bg-green-50 border-green-200'
                              : 'bg-[#121A2F] border-[#1E293B]'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className={`w-6 h-6 ${
                            doc.is_expired ? 'text-red-500' : 
                            doc.verified_by_admin ? 'text-green-500' : 'text-[#64748B]'
                          }`} />
                          <div>
                            <p className="font-medium text-white">{doc.document_name}</p>
                            <p className="text-sm text-[#64748B]">{getDocumentTypeLabel(doc.document_type)}</p>
                            {doc.expiry_date && (
                              <p className={`text-xs flex items-center gap-1 ${
                                doc.is_expired ? 'text-[#EF4444]' : 'text-[#64748B]'
                              }`}>
                                {doc.is_expired ? (
                                  <>
                                    <AlertTriangle className="w-3 h-3" />
                                    Expired: {new Date(doc.expiry_date).toLocaleDateString()}
                                  </>
                                ) : (
                                  <>
                                    <Clock className="w-3 h-3" />
                                    Valid until: {new Date(doc.expiry_date).toLocaleDateString()}
                                  </>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {doc.verified_by_admin && (
                            <span className="px-2 py-1 bg-[#10B981]/10 text-[#10B981] text-xs font-medium rounded flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Verified
                            </span>
                          )}
                          {doc.is_expired && (
                            <span className="px-2 py-1 bg-[#EF4444]/10 text-[#EF4444] text-xs font-medium rounded flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              Expired
                            </span>
                          )}
                          {!doc.verified_by_admin && !doc.is_expired && (
                            <span className="px-2 py-1 bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-medium rounded flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Pending Review
                            </span>
                          )}
                          <a 
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-[#3B82F6] text-sm font-medium"
                          >
                            View
                          </a>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="text-red-500 hover:text-[#EF4444] p-1"
                            title="Delete document"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
